import { useState } from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { TextField } from '../index';

import type { useTheme } from './theme';

// The single note composer (next/timeline-logger.md §9, Solution C) — a
// chat-style input + Send that drops a standalone note into the timeline feed,
// "come se avessimo loggato un'esercizio" (Antenore 2026-06-04). It is the
// ONLY note-authoring surface in the timeline logger: the per-entry 📝 menu is
// retired (existing notes still render read-only). Sits after the FocusPanel,
// before the TimelineList.
//
// Sends are rejected under 4 chars (matches the WorkoutNote schema floor); the
// button is disabled until then so an empty tap can't fire. a11y: 44pt send
// target + readable input ([[feedback_user_visual_accessibility]]).
const MIN_LEN = 4;
const MAX_LEN = 2000;

export function NoteComposer({
  onSend,
  t,
  theme,
}: {
  /** Appends the note to the draft. Returns true when accepted (≥4 chars),
   *  so the composer can clear its input only on a real send. */
  onSend: (text: string) => boolean;
  t: (k: string, opts?: Record<string, unknown>) => string;
  theme: ReturnType<typeof useTheme>;
}) {
  const styles = makeStyles(theme);
  const [text, setText] = useState('');
  const canSend = text.trim().length >= MIN_LEN;

  const send = () => {
    if (!canSend) return;
    const ok = onSend(text);
    if (ok) setText('');
  };

  return (
    <View style={styles.wrap}>
      <TextField
        theme={theme}
        multiline
        value={text}
        onChangeText={setText}
        placeholder={t('mobile.timeline.notePlaceholder', {
          defaultValue: 'Add a note to the timeline…',
        })}
        style={styles.input}
        maxLength={MAX_LEN}
        accessibilityLabel={t('mobile.timeline.notePlaceholder', {
          defaultValue: 'Add a note to the timeline…',
        })}
      />
      <Pressable
        accessibilityRole="button"
        accessibilityState={{ disabled: !canSend }}
        accessibilityLabel={t('mobile.timeline.noteSend', {
          defaultValue: 'Send note',
        })}
        disabled={!canSend}
        onPress={send}
        style={({ pressed }) => [
          styles.sendBtn,
          !canSend && styles.sendBtnDisabled,
          pressed && canSend && styles.sendBtnPressed,
        ]}
      >
        <Text style={[styles.sendText, !canSend && styles.sendTextDisabled]}>
          {t('mobile.timeline.noteSend', { defaultValue: 'Send note' })}
        </Text>
      </Pressable>
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      gap: s.s2,
    },
    // Layout only — box internals live in the <TextField> primitive.
    input: {
      flex: 1,
      minHeight: 44,
      maxHeight: 120,
    },
    sendBtn: {
      minHeight: 44,
      paddingHorizontal: s.s4,
      justifyContent: 'center',
      borderRadius: r.r2,
      backgroundColor: p.brand500,
    },
    sendBtnDisabled: { backgroundColor: p.surface2 },
    sendBtnPressed: { backgroundColor: p.brand600 },
    sendText: {
      color: p.inkOnBrand,
      fontSize: ty.sizes.sm,
      fontWeight: '700',
    },
    sendTextDisabled: { color: p.ink3 },
  });
}
